import { defineStore } from "pinia";
import { isEqual } from "lodash-es";

import { ingredientFilterUserPath, ingredientSearchUserPath } from "~/routes";
import { useIngredientTagStore } from "./ingredient-tag-store";
import { FOOD, NONFOOD } from "./constants";

const validDate = (v) => {
  return typeof v === "string" && v.match(/^\d{4}-\d\d-\d\d$/);
};

const defaults = {
  userId: null,
  searchEndpoint: null,

  loaded: false,
  loading: false,

  q: "",

  activeTags: [],
  ingredientTypes: [],

  // These params (beginDate, endDate, sortBy, sortDirection, archived) are supported by `ingredient_filter` but not `ingredient_search`.
  beginDate: null,
  endDate: null,
  sortBy: "updated_at",
  sortDirection: "desc",
  archived: false,

  // These params (scopeFilter, foodMode, showEmails) are supported by `ingredient_search` but not `ingredient_filter`.
  scopeFilter: null, // one of INGREDIENT_SEARCH_SCOPE_FILTERS
  foodMode: null, // null, "FOOD", or "NONFOOD"
  showEmails: false,

  visibleIngredients: [],
  totalEntries: 0,
  // Total archived ingredients for this user (independent of the current
  // filter), populated from each ingredient_filter response.
  archivedCount: 0,
  currentPage: 1,
  perPage: 20,

  requestId: 0,

  initialSettings: null,

  // Selection state for bulk actions
  selection: [],
  _snapshotOfSelectionArchiveState: {},
};

/* State keys which have an effect on the returned results mapped to their URLSearchParam key */
const queryParamMap = {
  q: "q",
  activeTags: "tags[]",
  ingredientTypes: "ingredient_types[]",
  perPage: "per_page",
  currentPage: "page",

  sortBy: "sort_by",
  sortDirection: "sort_direction",
  beginDate: "begin_date",
  endDate: "end_date",
  archived: "archived",

  excludeIngredientId: "exclude_ingredient_id",

  scopeFilter: "scope_filter",
  foodMode: "food_mode",
  showEmails: "show_emails",
};

// `archived` is intentionally excluded: the "Archived only" checkbox lives
// outside the advanced filter box, so it shouldn't count toward the
// hasAdvancedFilters signal that auto-opens that box.
const advancedFilterKeys = ["activeTags", "beginDate", "endDate", "ingredientTypes"];

const validSorts = ["name", "updated_at"];

const validFoodModeValues = [FOOD, NONFOOD];

export const useIngredientSearchStore = {
  /// Filtering one's own ingredients for the ingredient dashboard; at time of writing it is backed by the DB.
  ingredientDashboard() {
    return initIngredientSearchStore({
      storeName: "ingredientDashboard.ingredientSearch",
      getSearchEndpoint: (userId) => ingredientFilterUserPath(userId),
      unsupportedParams: ["scopeFilter", "foodMode", "showEmails"],
    });
  },

  /// Ingredient search for ingredient replacement; at time of writing it is backed by Elasticsearch.
  ingredientReplacementSearch({ foodMode = FOOD, showEmails = false, perPage = 12 }) {
    return initIngredientSearchStore({
      storeName: "ingredientReplacementSearch",
      getSearchEndpoint: (userId) => ingredientSearchUserPath(userId),
      foodMode,
      defaults: {
        perPage,
        showEmails,
        // No defaults for sort params; Elasticsearch filtering has its own match-score-based sorting.
        sortBy: null,
        sortDirection: null,
      },
      unsupportedParams: ["beginDate", "endDate", "sortBy", "sortDirection", "archived"],
    });
  },
};

const initIngredientSearchStore = (
  options = {
    storeName: "ingredientSearch",
    foodMode: null,
    defaults: {},
    getSearchEndpoint: (_user) => {
      throw new Error("Usage error: must implement getSearchEndpoint");
    },
    unsupportedParams: [],
  }
) => {
  const { foodMode, getSearchEndpoint } = options;

  let storeName = options.storeName;
  if (!storeName) {
    throw new Error("Usage error: storeName is required");
  }

  if (foodMode) {
    if (!validFoodModeValues.includes(foodMode)) {
      throw new Error(`Invalid foodMode: ${foodMode}`);
    } else if (options.unsupportedParams.includes("foodMode")) {
      throw new Error("Unsupported param: foodMode");
    }

    // Namespace the storeName to include the foodMode.
    storeName = `${storeName}[${foodMode}]`;
  }

  const checkUnsupportedParams = (params) => {
    for (const param of params) {
      if (options.unsupportedParams.includes(param)) throw new Error(`Unsupported param: ${param}`);
    }
  };
  const checkIfSortParamsAllowed = () => checkUnsupportedParams(["sortBy", "sortDirection"]);

  const getDefaults = () => {
    return {
      ...defaults,
      ...(options.defaults || {}),
      foodMode,
    };
  };

  const useStore = defineStore(storeName, {
    persist: {
      storage: sessionStorage,
      paths: [
        "userId",
        "searchEndpoint",
        "q",
        "activeTags",
        "ingredientTypes",
        "sortBy",
        "sortDirection",
        "beginDate",
        "endDate",
        "scopeFilter",
      ].filter((key) => {
        return !options.unsupportedParams.includes(key);
      }),
    },

    state: () => {
      return {
        ...getDefaults(),
        initialSettings: {},
      };
    },

    getters: {
      queryParamMap: (state) => {
        return queryParamMap;
      },

      /* Returns the keys that the user has configured that affect filters */
      configuredFilterKeys: (state) => {
        const pristine = { ...defaults, ...(state.initialSettings ?? {}) };

        const diff = Object.keys(queryParamMap)
          .filter((k) => !isEqual(state[k], pristine[k]))
          .map((k) => [k, [state[k], pristine[k]]]);

        return Object.fromEntries(diff);
      },

      hasQuery: (state) => {
        return state.q !== "";
      },

      hasAdvancedFilters: (state) => {
        return Object.keys(state.configuredFilterKeys).some((k) => advancedFilterKeys.includes(k));
      },

      hasAnyArchived: (state) => state.archivedCount > 0,

      requestQueryParams: (state) => {
        const p = new URLSearchParams();

        if (state.q !== "") {
          p.append(queryParamMap["q"], state.q);
        }

        state.activeTags.forEach((t) => {
          p.append(queryParamMap["activeTags"], t.name);
        });

        state.ingredientTypes.forEach((type) => {
          p.append(queryParamMap["ingredientTypes"], type);
        });

        p.append(queryParamMap["perPage"], state.perPage);
        p.append(queryParamMap["currentPage"], state.currentPage);

        if (state.sortBy) {
          p.append(queryParamMap["sortBy"], state.sortBy);
          p.append(queryParamMap["sortDirection"], state.sortDirection);
        }

        if (validDate(state.beginDate)) {
          p.append(queryParamMap["beginDate"], state.beginDate);
        }

        if (validDate(state.endDate)) {
          p.append(queryParamMap["endDate"], state.endDate);
        }

        if (state.archived) {
          p.append(queryParamMap["archived"], state.archived);
        }

        if (state.excludeIngredientId) {
          p.append(queryParamMap["excludeIngredientId"], state.excludeIngredientId);
        }

        if (state.scopeFilter) {
          p.append(queryParamMap["scopeFilter"], state.scopeFilter);
        }

        if (state.foodMode) {
          p.append(queryParamMap["foodMode"], state.foodMode);
        }

        if (state.showEmails) {
          p.append(queryParamMap["showEmails"], state.showEmails);
        }

        return p;
      },

      requestQueryString() {
        return this.requestQueryParams.toString();
      },

      hasSelection() {
        return this.selection.length > 0;
      },

      selectionCount() {
        return this.selection.length;
      },

      /// Per-id array of "archived" | "unarchived" | "unknown". "unknown" can
      /// occur for cross-page select-all when the row isn't loaded yet. Mirrors
      /// recipe-search-store; consumers use .includes(...) to derive button state.
      selectionArchiveState() {
        return this.selection.map((id) => this._snapshotOfSelectionArchiveState[id] || "unknown");
      },
    },

    actions: {
      assignInitial(values = {}, { patch = false } = {}) {
        this.initialSettings = { ...values };

        if (patch) {
          this.$patch(values);
        }
      },

      setUserId(userId) {
        if (this.userId !== null && userId !== this.userId) {
          this.$reset();
        }

        this.userId = userId;
        this.setSearchEndpoint(getSearchEndpoint(this.userId));
      },

      setSearchEndpoint(url) {
        this.searchEndpoint = url;
      },

      async loadInitial({ force = false } = {}) {
        if (force) {
          this.loaded = false;
        }
        if (!this.loaded) {
          return this.loadIngredientPage();
        }
        return true;
      },

      async loadIngredientPage(newPage) {
        if (newPage !== undefined) {
          this.currentPage = newPage;
        }

        this.loading = true;
        this.requestId += 1;
        const currentRequestId = this.requestId;

        const url = `${this.searchEndpoint}?${this.requestQueryString}`;

        try {
          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Accept": "application/json",
              "Content-Type": "application/json",
            },
            body: null,
          });

          const json = await response.json();

          if (currentRequestId === this.requestId) {
            this.visibleIngredients = json.ingredients;
            this.totalEntries = json.total_entries;
            this.perPage = json.per_page;
            this.currentPage = json.current_page;
            this.archivedCount = json.archived_count ?? 0;
            this.loaded = true;
            this.loading = false;

            // Self-heal: if we're in archived-only view but there are no
            // archived ingredients (e.g. user just restored the last one, or
            // landed here via a stale `?ingredient_archived=true` URL), the
            // "Archived only" checkbox is gated off and the user has no UI to
            // leave this view. Flip back to the active view automatically.
            if (this.archived && this.archivedCount === 0) {
              this.archived = false;
              return this.loadIngredientPage(1);
            }

            return true;
          } else {
            // Don't toggle .loading, the latest in-progress request should do that.
            // Return true though, so that the caller doesn't think it's an error.
            return true;
          }
        } catch (err) {
          console.error(`Error upon ingredientSearchStore fetch url=${url} ${err} ${err.stack}`);
          if (currentRequestId === this.requestId) {
            this.loading = false;
          }
        }

        return false;
      },

      setTagFilter(tags) {
        this.activeTags = tags;
        this.loadIngredientPage(1);
      },

      setIngredientTypes(types) {
        // The Vue component already collapses "all checked" to [] before emit, so the
        // store just stores what it receives.
        if (isEqual(this.ingredientTypes, types)) {
          return;
        }
        this.ingredientTypes = types;
        this.loadIngredientPage(1);
      },

      setQueryFilter(q) {
        if (this.q == q) {
          return;
        }

        this.q = q;
        this.loadIngredientPage(1);
      },

      setScopeFilter(value) {
        checkUnsupportedParams(["scopeFilter"]);
        if (this.scopeFilter === value) {
          return;
        }
        this.scopeFilter = value;
      },

      setDateFilter({ begin, end }) {
        checkUnsupportedParams(["beginDate", "endDate"]);
        this.beginDate = begin;
        this.endDate = end;

        this.loadIngredientPage(1);
      },

      setSort(sortBy, sortDirection) {
        checkIfSortParamsAllowed();
        this.sortBy = sortBy;
        if (sortDirection !== undefined) {
          this.sortDirection = sortDirection;
        }

        this.loadIngredientPage(1);
      },

      toggleSort(field) {
        checkIfSortParamsAllowed();
        if (this.sortBy === field) {
          this.setSort(field, this.sortDirection === "desc" ? "asc" : "desc");
        } else {
          this.setSort(field, "asc");
        }
      },

      setExcludeIngredientId(id) {
        checkUnsupportedParams(["excludeIngredientId"]);
        this.excludeIngredientId = id;
      },

      setArchived(value) {
        checkUnsupportedParams(["archived"]);
        if (this.archived === value) {
          return;
        }
        this.archived = value;
        this.loadIngredientPage(1);
      },

      // Sets the search from page's URLSearchParams. The opposite of requestQueryParams getter
      async setFromSearchParams(searchParams) {
        const newState = {};

        const defaultState = (key) => this.initialSettings[key] || defaults[key];

        if (searchParams.has(queryParamMap["q"])) {
          newState["q"] = searchParams.get(queryParamMap["q"]);
        }

        if (searchParams.has(queryParamMap["perPage"])) {
          newState["perPage"] =
            parseInt(searchParams.get(queryParamMap["perPage"])) || defaultState("perPage");
        }

        if (searchParams.has(queryParamMap["currentPage"])) {
          newState["currentPage"] =
            parseInt(searchParams.get(queryParamMap["currentPage"])) || defaultState("currentPage");
        }

        if (searchParams.has(queryParamMap["sortBy"])) {
          const sort = validSorts[searchParams.get(queryParamMap["sortBy"])]
            ? searchParams.get(queryParamMap["sortBy"])
            : defaultState("sortBy");
          newState["sortBy"] = sort;
        }

        if (searchParams.has(queryParamMap["sortDirection"])) {
          newState["sortDirection"] =
            searchParams.get(queryParamMap["sortDirection"]) == "asc" ? "asc" : "desc";
        }

        if (searchParams.has(queryParamMap["beginDate"])) {
          const date = validDate(searchParams.get(queryParamMap["beginDate"]))
            ? searchParams.get(queryParamMap["beginDate"])
            : defaultState("beginDate");
          newState["beginDate"] = date;
        }

        if (searchParams.has(queryParamMap["endDate"])) {
          const date = validDate(searchParams.get(queryParamMap["endDate"]))
            ? searchParams.get(queryParamMap["endDate"])
            : defaultState("endDate");
          newState["endDate"] = date;
        }

        if (searchParams.has(queryParamMap["scopeFilter"])) {
          newState["scopeFilter"] = searchParams.get(queryParamMap["scopeFilter"]);
        }

        if (searchParams.has(queryParamMap["activeTags"])) {
          const tagNames = searchParams.getAll(queryParamMap["activeTags"]);
          const tagStore = useIngredientTagStore();

          if (!tagStore.loaded) {
            tagStore.setUserId(this.userId);
            await tagStore.loadAllTags();
          }

          newState["activeTags"] = tagNames
            .map((name) => tagStore.getByName(name))
            .filter((tag) => tag);
        }

        if (searchParams.has(queryParamMap["archived"])) {
          newState["archived"] = searchParams.get(queryParamMap["archived"]) === "true";
        }

        if (searchParams.has(queryParamMap["ingredientTypes"])) {
          newState["ingredientTypes"] = searchParams.getAll(queryParamMap["ingredientTypes"]);
        }

        // Note: foodMode and excludeIngredientId are intentionally not persisted to the URL.

        this.resetSearch(newState);
      },

      resetResultsState() {
        this.visibleIngredients = [];
        this.totalEntries = 0;
        this.currentPage = 1;
      },

      resetSearch(overrides = {}) {
        const { userId, searchEndpoint, initialSettings } = { ...this };
        this.$patch({
          ...getDefaults(),
          ...initialSettings,
          userId: userId,
          searchEndpoint: searchEndpoint,
          initialSettings: initialSettings,
          ...overrides,
        });

        this.loadIngredientPage(this.currentPage);
      },

      // Clears filter state (q, tags, dates, archived, types) but preserves
      // sort and per-page so users don't lose their table ordering.
      resetFilters() {
        this.$patch({
          q: "",
          activeTags: [],
          ingredientTypes: [],
          beginDate: null,
          endDate: null,
          archived: false,
          currentPage: 1,
        });

        this.loadIngredientPage(1);
      },

      // Selection actions
      addToSelection(ingredientIds) {
        this._updateSnapshotOfSelectionArchiveState(ingredientIds);
        this.selection = Array.from(new Set([...this.selection, ...ingredientIds]));
      },

      removeFromSelection(ingredientIds) {
        this.selection = this.selection.filter((id) => !ingredientIds.includes(id));
        ingredientIds.forEach((id) => {
          delete this._snapshotOfSelectionArchiveState[id];
        });
      },

      clearSelection() {
        this.selection = [];
        this._snapshotOfSelectionArchiveState = {};
      },

      toggleSelection(ingredientId) {
        if (this.selection.includes(ingredientId)) {
          this.removeFromSelection([ingredientId]);
        } else {
          this.addToSelection([ingredientId]);
        }
      },

      async fetchSelection() {
        const response = await fetch(
          `${this.searchEndpoint}?format=ids&${this.requestQueryString}`,
          {
            method: "POST",
            headers: { Accept: "application/json" },
          }
        );

        return await response.json();
      },

      _updateSnapshotOfSelectionArchiveState(ingredientIds) {
        ingredientIds.forEach((id) => {
          if (this.selection.includes(id)) return;

          if (this.archived === false) {
            this._snapshotOfSelectionArchiveState[id] = "unarchived";
          } else if (this.archived === true) {
            this._snapshotOfSelectionArchiveState[id] = "archived";
          } else {
            const ingredient = this.visibleIngredients.find((i) => i.id === id);
            if (ingredient) {
              this._snapshotOfSelectionArchiveState[id] = ingredient.archived
                ? "archived"
                : "unarchived";
            } else {
              this._snapshotOfSelectionArchiveState[id] = "unknown";
            }
          }
        });
      },
    },
  });

  return useStore();
};
